/**
 * WEB-817 — turn `Event.imageUrls` (populated by PIP-235) into the `src` /
 * `srcset` / `sizes` triple an `<img>` needs, so each slot fetches a
 * right-sized rendition instead of the one full-size cover asset.
 *
 * ## What the column holds
 *
 * PIP-235 writes `{ thumbnail?, medium?, large? }` — renditions the SOURCE
 * published, never URLs we constructed. Two properties of that data drive
 * everything below:
 *
 *  - **Keys are omitted, not null-filled.** A source that published only a
 *    1024px rendition yields `{ large }` alone, so every key is optional and
 *    the whole map may be absent. `Event.imageUrl` is the guaranteed fallback.
 *  - **Within one map every entry is the same picture at a different width**
 *    (`buildTicketmasterImageUrls` restricts to the cover's aspect ratio;
 *    `buildEventbriteImageUrls` takes only the focal-point-cropped sized
 *    renditions). That is exactly the invariant `srcset` assumes, which is
 *    what makes the map safe to expand into one candidate list.
 *
 * ## Why `Event.imageUrl` never joins the `srcset`
 *
 * It is NOT guaranteed to be the same framing as the variants. On Eventbrite
 * the sized renditions are focal-point **cropped** while `imageUrl` is the
 * **uncropped** original; mixing them would let the browser swap crops
 * between breakpoints. So `imageUrl` is used only as the `src` of last
 * resort, when no variant survives — a structural rule here, not a
 * per-caller convention.
 *
 * ## Width descriptors: never claim more than the data guarantees
 *
 * A `w` descriptor is a promise about a resource's intrinsic width, and
 * PIP-235 persists the URL but not the width. Over-declaring a width makes
 * the browser pick a candidate too small for the slot — a visibly blurry
 * image. Under-declaring only makes it pick a larger one: wasted bytes, never
 * blur. So every descriptor here is a width the data *guarantees*:
 *
 *  - `medium` is banded as `[400, 800)` → at least **400w**.
 *  - `large` is banded as `[800, 2048]` → at least **800w**.
 *  - `thumbnail` is banded as `(0, 400)` → **no lower bound at all**, so it
 *    earns a descriptor only when its own URL declares one (see below) and is
 *    otherwise left out of the `srcset` rather than guessed at.
 *
 * Bands mirror `services/data-pipeline/src/lib/imageVariants.ts`; the
 * constants below are pinned to those boundaries by unit test.
 *
 * When a URL carries its own `w=` query parameter we read it and use the
 * exact value instead of the band floor. That is a *read* of something the
 * source put there, never an edit — the same technique (and the same
 * provider, Eventbrite, whose CDN URLs are HMAC-signed and would 403 if a
 * size parameter were appended) that PIP-235 used to band them in the first
 * place.
 */
import { toDisplayImageUrl } from './toDisplayImageUrl';

/** Size bands of `Event.imageUrls`, ascending. Mirrors `ImageUrls` in `@loci/types`. */
export type EventImageBand = 'thumbnail' | 'medium' | 'large';

/**
 * `Event.imageUrls` as it arrives on the wire. Every key optional — a present
 * key is a real published rendition, an absent one means the source published
 * nothing in that band (PIP-235 never null-fills).
 */
export interface EventImageUrls {
  thumbnail?: string | null;
  medium?: string | null;
  large?: string | null;
}

/** Ascending band order — the order candidates enter the `srcset`. */
const BANDS_ASCENDING: readonly EventImageBand[] = ['thumbnail', 'medium', 'large'];

/**
 * Narrowest width each band GUARANTEES, or `null` when it guarantees none.
 * Pinned to the `imageVariants.ts` boundaries: thumbnail `(0,400)`, medium
 * `[400,800)`, large `[800,2048]`. Thumbnail's `null` is the load-bearing
 * entry — see the file header on why it is not guessed at.
 */
const BAND_GUARANTEED_MIN_WIDTH_PX: Readonly<Record<EventImageBand, number | null>> = {
  thumbnail: null,
  medium: 400,
  large: 800,
};

/** Result of {@link buildEventImageSources}, spreadable straight onto an `<img>`. */
export interface EventImageSources {
  /**
   * Proxied URL for the `src` attribute. `''` when nothing renderable
   * survived — callers treat that as "render the placeholder".
   */
  src: string;
  /**
   * `srcset` value. Present only when at least two candidates have a
   * guaranteed width; a one-candidate `srcset` tells the browser nothing.
   */
  srcSet?: string;
  /**
   * Echoed back only alongside `srcSet`. Returning them together is
   * deliberate: an `srcset` with no `sizes` is specified to fall back to
   * `100vw`, which would make every small slot fetch the largest candidate —
   * the exact waste this helper exists to remove.
   */
  sizes?: string;
}

export interface EventImageSourcesInput {
  /** Raw `Event.imageUrl` — the guaranteed single-image fallback. */
  imageUrl?: string | null;
  /** Raw `Event.imageUrls` variant map. Absent/`null` → fallback path. */
  imageUrls?: EventImageUrls | null;
  /**
   * `sizes` describing the slot's rendered width (e.g. `'96px'`,
   * `'(max-width: 640px) 100vw, 400px'`). Required to get a `srcSet` back.
   */
  sizes?: string;
  /**
   * Band that best matches this slot, used to choose `src`. Resolution walks
   * UP from here first (a larger rendition is merely heavier; a smaller one
   * is blurry) before falling back to smaller bands. Defaults to `'large'`.
   */
  preferredBand?: EventImageBand;
}

/** One renderable candidate: a proxied URL plus the width we can stand behind. */
interface ImageCandidate {
  readonly band: EventImageBand;
  readonly src: string;
  /** Guaranteed intrinsic width in px, or `null` when the data guarantees none. */
  readonly width: number | null;
}

/**
 * Read the width a URL declares in its own `w=` query parameter.
 *
 * Reads only — never adds or edits one. Companion to
 * `readDeclaredWidthParam` in the data-pipeline: Eventbrite's actor emits
 * each rendition as a separately signed URL carrying its width
 * (`…&w=640&…&s=<signature>`). Returns `null` for an unparseable URL or a
 * missing / non-positive-integer `w`.
 *
 * Must run against the RAW provider URL, before proxying — `toDisplayImageUrl`
 * percent-encodes the original into a `src` parameter, which would hide `w`.
 */
function readDeclaredWidthPx(rawUrl: string): number | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }
  const raw = parsed.searchParams.get('w');
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const width = Number(raw);
  return width > 0 ? width : null;
}

/**
 * Expand the variant map into renderable candidates, ascending by band.
 *
 * Each entry is normalised through `toDisplayImageUrl` — the single
 * render-time CSP/proxy chokepoint — and dropped when that collapses it to
 * `''` (blank value or a host outside the proxy allowlist, which could only
 * ever be CSP-blocked).
 */
function collectCandidates(imageUrls: EventImageUrls | null | undefined): ImageCandidate[] {
  if (!imageUrls) return [];

  const candidates: ImageCandidate[] = [];
  for (const band of BANDS_ASCENDING) {
    const raw = imageUrls[band];
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;

    const src = toDisplayImageUrl(trimmed);
    if (src.length === 0) continue;

    candidates.push({
      band,
      src,
      // An exact declared width beats the band floor; the floor is the
      // conservative stand-in when the source declares nothing.
      width: readDeclaredWidthPx(trimmed) ?? BAND_GUARANTEED_MIN_WIDTH_PX[band],
    });
  }
  return candidates;
}

/**
 * Build the `srcset` value from candidates with a guaranteed width.
 *
 * Sorted ascending and de-duplicated on both URL and descriptor: a repeated
 * `w` makes the extra candidate unreachable, and a repeated URL is pure
 * payload. Returns `undefined` below two entries — one candidate carries no
 * more information than `src` alone.
 */
function buildSrcSet(candidates: readonly ImageCandidate[]): string | undefined {
  const sized = candidates
    .filter(
      (candidate): candidate is ImageCandidate & { width: number } => candidate.width !== null,
    )
    .sort((a, b) => a.width - b.width);

  const seenWidths = new Set<number>();
  const seenSrcs = new Set<string>();
  const entries: string[] = [];
  for (const candidate of sized) {
    if (seenWidths.has(candidate.width) || seenSrcs.has(candidate.src)) continue;
    seenWidths.add(candidate.width);
    seenSrcs.add(candidate.src);
    entries.push(`${candidate.src} ${candidate.width}w`);
  }

  return entries.length >= 2 ? entries.join(', ') : undefined;
}

/**
 * Choose the `src`: the preferred band when present, otherwise the nearest
 * LARGER band, otherwise the nearest smaller one.
 *
 * Upward-first because the two misses are not symmetric — an oversized
 * rendition costs bytes, an undersized one is visibly blurry. Any variant
 * beats `Event.imageUrl`: variants are guaranteed to share the cover's
 * framing, while `imageUrl` on Eventbrite is the uncropped original.
 */
function pickSrc(
  candidates: readonly ImageCandidate[],
  preferredBand: EventImageBand,
): string | undefined {
  const preferredIndex = BANDS_ASCENDING.indexOf(preferredBand);
  const byBand = new Map(candidates.map((candidate) => [candidate.band, candidate.src]));

  for (let i = preferredIndex; i < BANDS_ASCENDING.length; i += 1) {
    const src = byBand.get(BANDS_ASCENDING[i]);
    if (src !== undefined) return src;
  }
  for (let i = preferredIndex - 1; i >= 0; i -= 1) {
    const src = byBand.get(BANDS_ASCENDING[i]);
    if (src !== undefined) return src;
  }
  return undefined;
}

/**
 * Pick the single best RAW (un-proxied) variant URL for a consumer that
 * cannot use the same-origin proxy — social crawlers reading `og:image`,
 * above all, since `toDisplayImageUrl` returns a *relative* proxy path and a
 * crawler needs an absolute, directly fetchable URL.
 *
 * Band resolution is the same upward-first walk as {@link buildEventImageSources}
 * (prefer the requested band, then larger, then smaller), falling back to
 * `imageUrl`. Unlike the render path this applies no proxy allowlist: the
 * consumer fetches the provider directly, so our CSP is not involved.
 *
 * Returns `null` when there is nothing to point at.
 */
export function pickRawEventImageUrl({
  imageUrl,
  imageUrls,
  preferredBand = 'large',
}: Omit<EventImageSourcesInput, 'sizes'>): string | null {
  const rawCandidates: ImageCandidate[] = [];
  for (const band of BANDS_ASCENDING) {
    const raw = imageUrls?.[band];
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    // `width` is unused on this path (no `srcset` is built) but keeps the
    // candidate shape shared with the render path.
    rawCandidates.push({ band, src: trimmed, width: null });
  }

  const picked = pickSrc(rawCandidates, preferredBand);
  if (picked !== undefined) return picked;

  const fallback = imageUrl?.trim();
  return fallback ? fallback : null;
}

/**
 * Resolve an event's imagery to `{ src, srcSet?, sizes? }`.
 *
 * Safe on every shape the wire can produce: no `imageUrls`, a partial map, a
 * map whose every entry is an unproxiable host, or nothing at all (→ `src`
 * is `''` and the caller renders its placeholder).
 */
export function buildEventImageSources({
  imageUrl,
  imageUrls,
  sizes,
  preferredBand = 'large',
}: EventImageSourcesInput): EventImageSources {
  const candidates = collectCandidates(imageUrls);
  // Fallback is reached only when NO variant is renderable — never merged
  // into the candidate list, so it can never enter the `srcset` (file header).
  const src = pickSrc(candidates, preferredBand) ?? toDisplayImageUrl(imageUrl);

  if (src.length === 0) return { src: '' };

  const srcSet = sizes === undefined ? undefined : buildSrcSet(candidates);
  return srcSet === undefined ? { src } : { src, srcSet, sizes };
}
